import React, { useState } from 'react';
import { Alert, Badge, Button, Group, Loader, Paper, SimpleGrid, Stack, Text, Tooltip } from '@mantine/core';
import { LineChart } from '@mui/x-charts/LineChart';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';
import { formatPerformanceAmount, formatPerformanceReturn, InvestmentPerformance, InvestmentPerformancePoint, InvestmentPerformanceTarget, isPerformanceStale, performanceReasonText, PerformanceSubject } from '../helpers/investmentPerformance';
import { useInvestmentPerformance, useInvestmentPerformanceTotal } from '../hooks/useInvestmentPerformance';

function Metric({ label, value }: { label: string; value: string }) {
  return <div><Text size="xs" c="dimmed">{label}</Text><Text fw={600}>{value}</Text></div>;
}

const positiveColor = '#13866e';
const negativeColor = '#c92a2a';
const returnColor = (ytdReturn: string | null) => ytdReturn !== null && Number(ytdReturn) < 0 ? negativeColor : positiveColor;

interface SparklineProps { series: { date: string; value: number }[]; color: string; baseline?: number | 'min'; format: (value: number) => string; label: string; width?: number; height?: number }

// Fewer than two values are not a trend, so nothing is drawn.
function Sparkline({ series, color, baseline = 'min', format, label, width = 110, height = 32 }: SparklineProps) {
  if (series.length < 2) return null;
  return <div style={{ width, height, flexShrink: 0 }} aria-label={label}>
    <SparkLineChart
      data={series.map(point => point.value)}
      width={width}
      height={height}
      color={color}
      area
      baseline={baseline}
      curve="linear"
      showTooltip
      showHighlight
      margin={2}
      xAxis={{ data: series.map(point => new Date(`${point.date}T00:00:00Z`)), scaleType: 'utc', valueFormatter: (date: Date) => date.toISOString().slice(0, 10) }}
      valueFormatter={value => value === null ? 'Unavailable' : format(value)}
    />
  </div>;
}

// Inline trend of the cumulative YTD return. Dates without a value are skipped
// rather than drawn as zero.
export function PerformanceSparkline({ points, width, height }: { points: InvestmentPerformancePoint[]; width?: number; height?: number }) {
  const known = points.filter(point => point.ytdReturn !== null).map(point => ({ date: point.date, value: Number(point.ytdReturn) * 100 }));
  return <Sparkline series={known} color={known.length ? returnColor(String(known[known.length - 1].value)) : positiveColor} baseline={0} format={value => `${value.toFixed(2)}%`} label="Daily cumulative YTD return trend" width={width} height={height} />;
}

const formatTotal = (value: number) => `${value.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} KWD`;

// Trend of the combined daily value of the given investments, for accordion
// headers. Renders nothing for viewers without performance access.
export function TotalValueSparkline({ investments, width = 140, height = 36 }: { investments: InvestmentPerformanceTarget[]; width?: number; height?: number }) {
  const { enabled, loading, points, year, asOfDate } = useInvestmentPerformanceTotal(investments);
  if (!enabled || loading || points.length < 2) return null;
  const first = points[0].value;
  const last = points[points.length - 1].value;
  return <Tooltip label={`Total value ${year} YTD · ${formatTotal(last)} as of ${asOfDate ?? points[points.length - 1].date}`} withArrow>
    <Group gap="xs" wrap="nowrap">
      <Sparkline series={points} color={last < first ? negativeColor : positiveColor} format={formatTotal} label="Total daily value trend" width={width} height={height} />
      <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>{formatTotal(last)}</Text>
    </Group>
  </Tooltip>;
}

export function PerformanceSummary({ summary, compact = false }: { summary: InvestmentPerformance; compact?: boolean }) {
  const available = summary.status !== 'UNAVAILABLE';
  const provisional = summary.status === 'PROVISIONAL';
  const reasons = [...new Set(summary.reasons.map(performanceReasonText))].join(' ');
  // Informational notes are separate from the warning banner and never imply review.
  const notes = [...new Set(summary.notes ?? [])].join(' ');
  const firstInvestedDate = summary.firstInvestedDate ?? summary.periodStart;
  const badges = <>
    {summary.status !== 'READY' && <Tooltip label={reasons || 'See the source notes in the holding details.'} multiline w={300}><Badge size="xs" color={provisional ? 'yellow' : 'gray'}>{provisional ? 'Provisional' : 'Unavailable'}</Badge></Tooltip>}
    {isPerformanceStale(summary) && <Badge size="xs" color="orange">Stale</Badge>}
  </>;
  if (compact) {
    // Sparkline plus the headline figure; the full wording lives in the tooltip.
    return <Tooltip label={`${summary.subjectType === 'FUND_HOLDING' ? 'Your holding' : 'Portfolio'} YTD return ${summary.year} · as of ${summary.asOfDate} · KWD`} withArrow>
      <Group gap="sm" justify="center" wrap="nowrap">
        {available && <PerformanceSparkline points={summary.points} />}
        <Stack gap={0} align="flex-start">
          <Text size="sm" fw={600} c={available ? returnColor(summary.ytdReturn) : 'dimmed'}>{available ? formatPerformanceReturn(summary.ytdReturn) : '—'}</Text>
          <Text size="xs" c="dimmed">YTD {summary.year}</Text>
        </Stack>
        {badges}
      </Group>
    </Tooltip>;
  }
  return <Stack gap="xs">
    <Group gap="xs" justify="space-between" wrap="nowrap">
      <Group gap="xs">
        <Text size="sm" fw={600}>{summary.subjectType === 'FUND_HOLDING' ? 'Your holding YTD return' : 'Portfolio YTD return'} · {summary.year}</Text>
        {badges}
      </Group>
      {available && <PerformanceSparkline points={summary.points} width={180} height={44} />}
    </Group>
    <SimpleGrid cols={{ base: 1, sm: 3 }}>
      <Metric label="Cash-flow-adjusted return" value={available ? formatPerformanceReturn(summary.ytdReturn) : '—'} />
      <Metric label="YTD investment gain / loss" value={available ? formatPerformanceAmount(summary.gainLoss) : '—'} />
      <Metric label="Latest value" value={formatPerformanceAmount(summary.closingValue)} />
    </SimpleGrid>
    <Text size="xs" c="dimmed">As of {summary.asOfDate} · KWD · Includes currency movements</Text>
    {firstInvestedDate > `${summary.year}-01-01` && <Text size="xs" c="dimmed">Since first investment on {firstInvestedDate}</Text>}
    {notes && <Text size="xs" c="dimmed" fs="italic" data-testid="performance-notes">{notes}</Text>}
    {reasons && <Alert color={provisional ? 'yellow' : 'gray'}>{reasons}</Alert>}
  </Stack>;
}

export function DailyPerformanceChart({ points }: { points: InvestmentPerformancePoint[] }) {
  const [metric, setMetric] = useState<'value' | 'ytdReturn'>('ytdReturn');
  return <Stack gap="xs">
    <Group gap="xs">
      <Button size="xs" variant={metric === 'ytdReturn' ? 'filled' : 'light'} onClick={() => setMetric('ytdReturn')}>Return %</Button>
      <Button size="xs" variant={metric === 'value' ? 'filled' : 'light'} onClick={() => setMetric('value')}>Value in KWD</Button>
    </Group>
    <div style={{ width: '100%', minWidth: 0 }} aria-label={metric === 'value' ? 'Daily investment value in KWD' : 'Daily cumulative YTD return'}>
      <LineChart
        height={250}
        skipAnimation
        xAxis={[{ data: points.map(point => new Date(`${point.date}T00:00:00Z`)), scaleType: 'utc', domainLimit: 'strict', valueFormatter: (date: Date, context) => context.location === 'tick' ? date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' }) : date.toISOString().slice(0, 10) }]}
        yAxis={[{ width: 85, valueFormatter: (value: number) => metric === 'value' ? Number(value).toLocaleString('en-US') : `${Number(value).toFixed(1)}%` }]}
        series={[{ id: metric, label: metric === 'value' ? 'Value (KWD)' : 'YTD return', data: points.map(point => point[metric] === null ? null : Number(point[metric]) * (metric === 'ytdReturn' ? 100 : 1)), showMark: false, connectNulls: false, curve: 'linear', color: '#13866e', valueFormatter: value => value === null ? 'Unavailable' : metric === 'value' ? `${value.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} KWD` : `${value.toFixed(2)}%` }]}
      />
    </div>
    <Text size="xs" c="dimmed">Daily linked estimate in KWD, adjusted for external cash flows and including currency movements. Review the source notes for fee, income and reconciliation limitations. Missing dates appear as gaps.</Text>
  </Stack>;
}

export default function InvestmentPerformancePanel({ subject, investmentId, compact = false }: { subject: PerformanceSubject; investmentId?: string; compact?: boolean }) {
  const { enabled, summary, loading, error, year } = useInvestmentPerformance(subject, investmentId);
  const [expanded, setExpanded] = useState(false);
  if (!enabled) return null;
  if (loading) return <Group gap="xs" my="sm"><Loader size="xs" /><Text size="xs" c="dimmed">Loading YTD performance…</Text></Group>;
  if (error) return <Alert color="yellow" my="sm">{error}</Alert>;
  if (!summary) return <Text size="xs" c="dimmed" my="sm">{year} YTD return is not available yet.</Text>;
  if (compact) return <PerformanceSummary summary={summary} compact />;
  // Portfolio documents carry no `distributions` field at all (null still means
  // "unavailable" on fund holdings), so the tile is omitted rather than shown as a dash or zero.
  const hasDistributions = summary.distributions !== undefined;
  return <Paper withBorder p="md" my="md" radius="md">
    <PerformanceSummary summary={summary} />
    <SimpleGrid cols={{ base: 1, sm: hasDistributions ? 4 : 3 }} mt="sm">
      <Metric label="Opening value" value={formatPerformanceAmount(summary.openingValue)} />
      <Metric label="Contributions" value={formatPerformanceAmount(summary.contributions)} />
      <Metric label="Withdrawals" value={formatPerformanceAmount(summary.withdrawals)} />
      {hasDistributions && <Metric label="Distributions" value={formatPerformanceAmount(summary.distributions ?? null)} />}
    </SimpleGrid>
    {summary.points.length > 0 && summary.status !== 'UNAVAILABLE' && <Button mt="sm" variant="subtle" size="xs" onClick={() => setExpanded(value => !value)} aria-expanded={expanded}>{expanded ? 'Hide daily history' : 'Show daily history'}</Button>}
    {expanded && summary.status !== 'UNAVAILABLE' && summary.points.length > 0 && <div style={{ marginTop: 12 }}><DailyPerformanceChart points={summary.points} /></div>}
  </Paper>;
}
